import React, { useEffect, useState } from 'react';
import {
    CheckCircle2,
    Rocket,
    Target,
    TrendingUp,
    Camera,
    Sparkles,
    BarChart3,
    HeartHandshake,
    Workflow,
    Plus,
    Minus,
    Handshake,
    Inbox,
    Bot,
    Zap,
    ShieldCheck,
    Calendar
} from 'lucide-react';

/* Reusable Primitives */

const SectionTag = ({ icon: IconComp, label, color = "indigo" }) => {
    const colorClasses = {
        indigo: "bg-indigo-500/10 border-indigo-500/20 text-indigo-300",
        emerald: "bg-emerald-500/10 border-emerald-500/20 text-emerald-300",
        amber: "bg-amber-500/10 border-amber-500/20 text-amber-300",
        purple: "bg-purple-500/10 border-purple-500/20 text-purple-300"
    };
    return (
        <div className={`inline-flex items-center gap-2 px-3 py-1 mb-6 rounded-full border text-xs sm:text-sm font-semibold tracking-wide uppercase ${colorClasses[color]}`}>
            <IconComp className="w-4 h-4" /> {label}
        </div>
    );
};

const Check = ({ children }) => (
    <li className="flex gap-3 items-start">
        <CheckCircle2 className="w-5 h-5 mt-0.5 flex-shrink-0 text-emerald-400" />
        <span className="leading-relaxed text-slate-200 text-sm">{children}</span>
    </li>
);

const StatCard = ({ value, label, sub, accent = "indigo" }) => {
    const colors = {
        indigo: "from-indigo-900/40 to-slate-900/80 border-indigo-500/30",
        emerald: "from-emerald-900/40 to-slate-900/80 border-emerald-500/30",
        purple: "from-purple-900/40 to-slate-900/80 border-purple-500/30",
        amber: "from-amber-900/40 to-slate-900/80 border-amber-500/30"
    };
    return (
        <div className={`bg-gradient-to-br ${colors[accent]} border rounded-2xl p-4 sm:p-5 text-center h-full flex flex-col justify-center overflow-hidden`}>
            <div className="text-lg sm:text-xl md:text-2xl font-black text-white mb-1 whitespace-nowrap">{value}</div>
            <div className="text-xs sm:text-sm font-semibold text-white/80">{label}</div>
            {sub && <div className="text-[10px] sm:text-xs text-white/50 mt-2">{sub}</div>}
        </div>
    );
};

const StartCTA = ({ label = "Let's start the process" }) => (
    <div className="flex justify-center">
        <a
            href="mailto:suresh@socialseo.in?subject=DYOD%20Content%20Engine%2C%20Let%27s%20start"
            className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full bg-indigo-500 hover:bg-indigo-400 transition text-white font-bold text-sm sm:text-base shadow-lg shadow-indigo-500/30"
        >
            <Rocket className="w-4 h-4" /> {label}
        </a>
    </div>
);

const AccordionItem = ({ title, body, open, onToggle }) => (
    <div className="rounded-xl border border-white/10 bg-white/5 overflow-hidden">
        <button
            onClick={onToggle}
            className="w-full flex items-start justify-between gap-3 text-left p-4 sm:p-5 hover:bg-white/5 transition"
        >
            <span className="font-bold text-white text-sm sm:text-base leading-snug">{title}</span>
            <span className="flex-shrink-0 mt-0.5">
                {open ? <Minus className="w-5 h-5 text-indigo-300" /> : <Plus className="w-5 h-5 text-indigo-300" />}
            </span>
        </button>
        {open && (
            <div className="px-4 sm:px-5 pb-5 text-slate-300 text-sm leading-relaxed">
                {body}
            </div>
        )}
    </div>
);

/* Main Component */
export default function KislayContentEngine() {

    useEffect(() => {
        document.title = "DYOD | Content Engine Proposal";
        window.scrollTo(0, 0);
    }, []);

    const [openIdx, setOpenIdx] = useState(null);
    const toggle = (i) => setOpenIdx(openIdx === i ? null : i);

    const faqs = [
        {
            title: "1. Will the AI clone read as fake?",
            body: "No. We train it on a real face from a single 25-minute recording, not a stock avatar. Benchmark: Kush Desai's account has thousands of comments and not one flagged the content as AI. Same protocol, applied to DYOD."
        },
        {
            title: "2. Why a company page instead of personal brand?",
            body: "You said it on the call. This is the agency's brand, not yours. Your ICP buys from a company they can hire, not a creator they have to befriend. Every script, caption, and funnel asset routes through DYOD."
        },
        {
            title: "3. The team-member face isn't finalised yet. Does that block the start?",
            body: "No. Audit, scripting structure, ICP lock, and funnel design happen Weeks 1 and 2 regardless. The clone-training recording is Week 3. You have a clean window to decide who becomes the face."
        },
        {
            title: "4. How fast before qualified leads land?",
            body: "Month 1 is build. Month 2 is publish plus funnel install. Month 3 is when the curve compounds and leads land. Numerical KPI targets get locked during onboarding once we audit your existing funnel and ICP intake."
        }
    ];

    return (
        <div className="font-sans leading-relaxed text-white overflow-x-hidden selection:bg-indigo-500/30 pb-20 pt-36 sm:pt-40 relative z-10">

            {/* Floating Header */}
            <header className="fixed top-0 left-0 right-0 z-50 p-4 sm:p-6 flex justify-between items-center bg-transparent pointer-events-none">
                <div className="bg-white/10 backdrop-blur-lg border border-white/10 px-3 sm:px-4 py-2 rounded-full pointer-events-auto shadow-lg">
                    <span className="font-bold tracking-tight text-white text-sm sm:text-base">Social<span className="text-indigo-400">SEO</span></span>
                    <span className="text-white/30 mx-1.5 sm:mx-2">/</span>
                    <span className="text-white/70 font-medium text-xs sm:text-sm">DYOD Content Engine</span>
                </div>
                <div className="bg-indigo-500/20 backdrop-blur-lg border border-indigo-500/30 px-2.5 sm:px-3 py-1 rounded-full pointer-events-auto">
                    <span className="text-indigo-300 text-[10px] sm:text-xs font-bold tracking-widest uppercase">Proposal</span>
                </div>
            </header>

            <main className="max-w-[1120px] mx-auto px-4 space-y-20 sm:space-y-28">

                {/* HERO */}
                <section className="text-center pt-6 sm:pt-10">
                    <SectionTag icon={Rocket} label="DYOD Content Engine · 2026-06-01" />
                    <h1 className="text-4xl sm:text-5xl md:text-7xl font-black tracking-tight mb-6 bg-clip-text text-transparent bg-gradient-to-r from-white via-indigo-200 to-indigo-400 leading-tight pb-2">
                        DYOD's content engine.<br />Built, run, and shipped by us.
                    </h1>
                    <p className="text-base sm:text-xl md:text-2xl text-white/60 max-w-3xl mx-auto leading-relaxed mb-10">
                        Done-for-you short-form distribution for <a className="text-indigo-300 hover:text-indigo-200" target="_blank" rel="noreferrer noopener" href="https://www.instagram.com/doyourownads/">@doyourownads</a>. AI clone of your designated team member, trained on a single 25-minute recording, fanned across 5 platforms with a complete funnel underneath.
                    </p>

                    <StartCTA />

                    <div className="grid grid-cols-2 md:grid-cols-4 max-w-3xl mx-auto gap-3 sm:gap-4 mt-10">
                        <StatCard value="₹3,00,000" label="One offer, 3 months" sub="all-inclusive" accent="indigo" />
                        <StatCard value="90 videos" label="Shipped over 3 months" sub="~7 to 8 per week" accent="emerald" />
                        <StatCard value="25 min" label="Recording on your end" sub="one session, that's it" accent="purple" />
                        <StatCard value="5 platforms" label="Distribution surface" sub="IG, YT Shorts, LI, TikTok, FB" accent="amber" />
                    </div>
                </section>

                {/* WHAT'S IN — visual cards, minimal text */}
                <section>
                    <div className="text-center mb-10">
                        <SectionTag icon={Camera} label="What's in" />
                        <h2 className="text-3xl sm:text-4xl font-black mb-4">The DYOD content engine, at a glance</h2>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5 max-w-5xl mx-auto">
                        <div className="rounded-2xl border border-indigo-500/30 bg-indigo-900/10 p-5 sm:p-6">
                            <Bot className="w-8 h-8 text-indigo-300 mb-3" />
                            <h3 className="text-base sm:text-lg font-bold text-white mb-1.5">AI clone, human-real</h3>
                            <p className="text-slate-300 text-sm leading-snug">Trained on 25 minutes of your team-member face. Output reads as a real person.</p>
                        </div>
                        <div className="rounded-2xl border border-indigo-500/30 bg-indigo-900/10 p-5 sm:p-6">
                            <Sparkles className="w-8 h-8 text-indigo-300 mb-3" />
                            <h3 className="text-base sm:text-lg font-bold text-white mb-1.5">Scripting + ideation</h3>
                            <p className="text-slate-300 text-sm leading-snug">90 scripts written from your AI-automation use cases, tagged to your ICP.</p>
                        </div>
                        <div className="rounded-2xl border border-indigo-500/30 bg-indigo-900/10 p-5 sm:p-6">
                            <Camera className="w-8 h-8 text-indigo-300 mb-3" />
                            <h3 className="text-base sm:text-lg font-bold text-white mb-1.5">Editing + posting</h3>
                            <p className="text-slate-300 text-sm leading-snug">Cut, captioned, scheduled, and shipped across all 5 platforms by our team.</p>
                        </div>
                        <div className="rounded-2xl border border-indigo-500/30 bg-indigo-900/10 p-5 sm:p-6">
                            <Workflow className="w-8 h-8 text-indigo-300 mb-3" />
                            <h3 className="text-base sm:text-lg font-bold text-white mb-1.5">Complete funnel</h3>
                            <p className="text-slate-300 text-sm leading-snug">Landing page, DM auto-reply, lead capture, nurture, calendar, all installed.</p>
                        </div>
                        <div className="rounded-2xl border border-indigo-500/30 bg-indigo-900/10 p-5 sm:p-6">
                            <HeartHandshake className="w-8 h-8 text-indigo-300 mb-3" />
                            <h3 className="text-base sm:text-lg font-bold text-white mb-1.5">Dedicated team</h3>
                            <p className="text-slate-300 text-sm leading-snug">Editor, scripter, and account manager assigned to DYOD specifically.</p>
                        </div>
                        <div className="rounded-2xl border border-indigo-500/30 bg-indigo-900/10 p-5 sm:p-6">
                            <Inbox className="w-8 h-8 text-indigo-300 mb-3" />
                            <h3 className="text-base sm:text-lg font-bold text-white mb-1.5">Full management</h3>
                            <p className="text-slate-300 text-sm leading-snug">DMs surfaced into a daily digest. Comments triaged. Engagement compounded.</p>
                        </div>
                    </div>
                </section>

                {/* ICP profile — slim */}
                <section>
                    <div className="max-w-4xl mx-auto rounded-2xl border border-purple-500/30 bg-purple-900/10 p-6 sm:p-8">
                        <div className="flex items-start gap-4">
                            <div className="bg-purple-500/20 p-3 rounded-xl text-purple-300 shrink-0">
                                <Target className="w-6 h-6" />
                            </div>
                            <div>
                                <div className="text-purple-300 text-xs font-bold uppercase tracking-widest mb-2">ICP profile</div>
                                <h3 className="text-xl sm:text-2xl font-black text-white mb-3">US + Canada local-business operators</h3>
                                <p className="text-slate-300 text-sm sm:text-base leading-relaxed">
                                    Home services, health, legal, real estate. Owner-operators looking to install AI plus paid-ads systems without hiring an in-house team. Every script, hook, and caption is written for this one person. Targeting locked during onboarding.
                                </p>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Numbers we track — list only */}
                <section>
                    <div className="text-center mb-10">
                        <SectionTag icon={BarChart3} label="How we measure" color="emerald" />
                        <h2 className="text-3xl sm:text-4xl font-black mb-4">The numbers that matter</h2>
                    </div>

                    <div className="max-w-3xl mx-auto rounded-2xl border-2 border-emerald-500/40 bg-emerald-900/10 p-6 sm:p-8 backdrop-blur shadow-[0_0_40px_-10px_rgba(16,185,129,0.3)]">
                        <ul className="space-y-3">
                            <li className="flex gap-3 items-center"><Zap className="w-5 h-5 text-emerald-300 flex-shrink-0" /><span className="text-white font-semibold text-sm sm:text-base">ICP-tagged weekly views</span></li>
                            <li className="flex gap-3 items-center"><Zap className="w-5 h-5 text-emerald-300 flex-shrink-0" /><span className="text-white font-semibold text-sm sm:text-base">Qualified leads booked</span></li>
                            <li className="flex gap-3 items-center"><Zap className="w-5 h-5 text-emerald-300 flex-shrink-0" /><span className="text-white font-semibold text-sm sm:text-base">Qualified discovery calls</span></li>
                            <li className="flex gap-3 items-center"><Zap className="w-5 h-5 text-emerald-300 flex-shrink-0" /><span className="text-white font-semibold text-sm sm:text-base">DMs from in-ICP accounts</span></li>
                        </ul>
                        <p className="text-emerald-50/80 text-xs sm:text-sm mt-5 leading-relaxed">
                            We audit your existing funnel and ICP intake in Week 1, then lock the exact numerical KPI per metric during onboarding.
                        </p>
                    </div>
                </section>

                {/* Why us — short, Kush + SMM Gyaan */}
                <section>
                    <div className="text-center mb-10">
                        <SectionTag icon={Handshake} label="Why we're the right team" color="emerald" />
                        <h2 className="text-3xl sm:text-4xl font-black mb-4">Two profiles you already saw</h2>
                        <p className="text-slate-400 max-w-2xl mx-auto text-sm sm:text-base">
                            The two accounts we walked through on the June 1 call. Same mechanism, different ICPs.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5 sm:gap-6 max-w-5xl mx-auto">
                        <div className="rounded-2xl border border-emerald-500/30 bg-emerald-900/10 p-6 sm:p-7">
                            <div className="text-emerald-300 text-xs font-bold uppercase tracking-widest mb-2">Kush Desai · AI clone</div>
                            <h3 className="text-2xl font-black text-white mb-3">28 to 24K followers in 4 months</h3>
                            <p className="text-slate-300 text-sm leading-relaxed">
                                AI clone built from a single recording session. Hindi-English AI tools niche. Thousands of comments, zero flagged the content as AI.
                            </p>
                        </div>
                        <div className="rounded-2xl border border-emerald-500/30 bg-emerald-900/10 p-6 sm:p-7">
                            <div className="text-emerald-300 text-xs font-bold uppercase tracking-widest mb-2">smmguy · founder-led</div>
                            <h3 className="text-2xl font-black text-white mb-3">Multi-platform compounding engine</h3>
                            <p className="text-slate-300 text-sm leading-relaxed">
                                Same playbook with a real face. Scripted, edited, and posted by us across IG, YouTube Shorts, and LinkedIn for an Indian wealth-education ICP.
                            </p>
                        </div>
                    </div>
                </section>

                {/* THE OFFER */}
                <section id="offer">
                    <div className="text-center mb-10">
                        <SectionTag icon={TrendingUp} label="The offer" color="indigo" />
                        <h2 className="text-3xl sm:text-4xl font-black mb-4">One offer, all in</h2>
                    </div>

                    <div className="max-w-2xl mx-auto rounded-3xl border-2 border-indigo-500/50 bg-gradient-to-br from-indigo-900/30 to-slate-900/80 backdrop-blur p-8 sm:p-10 shadow-[0_0_50px_-10px_rgba(99,102,241,0.4)]">
                        <div className="text-center pb-6 border-b border-white/10 mb-6">
                            <div className="inline-block px-3 py-1 rounded-full text-xs font-bold uppercase tracking-widest bg-indigo-500/15 text-indigo-300 mb-3">
                                DYOD Content Engine
                            </div>
                            <div className="text-4xl sm:text-5xl md:text-6xl font-black text-white mb-1 whitespace-nowrap">₹3,00,000</div>
                            <div className="text-sm text-white/60">for 3 months, all-inclusive</div>
                        </div>

                        <ul className="space-y-3 mb-8">
                            <Check>90 videos shipped across 3 months</Check>
                            <Check>AI clone of your designated team member, reads as a real human</Check>
                            <Check>Posted across all 5 platforms: IG, YouTube Shorts, LinkedIn, TikTok, Facebook Reels</Check>
                            <Check>Done-for-you scripting, recording, editing, posting, management</Check>
                            <Check>Complete funnel: landing page, DM auto-reply, lead capture, nurture, calendar</Check>
                            <Check>Single 25-minute recording session on your end</Check>
                            <Check>Dedicated team assigned to DYOD</Check>
                        </ul>

                        <StartCTA />
                    </div>
                </section>

                {/* GUARANTEE BLOCK */}
                <section>
                    <div className="max-w-3xl mx-auto rounded-3xl border-2 border-amber-500/50 bg-gradient-to-br from-amber-900/20 to-slate-900/60 p-6 sm:p-8 md:p-10 backdrop-blur shadow-[0_0_50px_-10px_rgba(245,158,11,0.4)]">
                        <div className="flex items-start gap-4 sm:gap-5">
                            <div className="bg-amber-500/20 p-3 rounded-xl shrink-0">
                                <ShieldCheck className="w-7 h-7 sm:w-8 sm:h-8 text-amber-300" />
                            </div>
                            <div className="flex-1">
                                <div className="text-amber-300 text-[10px] sm:text-xs font-bold uppercase tracking-widest mb-2">The guarantee</div>
                                <h2 className="text-2xl sm:text-3xl md:text-4xl font-black text-white mb-4 leading-tight">10,000 followers in 90 days, or 100% money back.</h2>
                                <p className="text-amber-50/90 text-sm sm:text-base leading-relaxed mb-3">
                                    If at the end of 90 days the engine has not delivered 10,000 followers to DYOD, 100% of fees paid refund in full. No carve-outs, no partial credits, no scope renegotiation.
                                </p>
                                <p className="text-amber-50/70 text-xs sm:text-sm leading-relaxed italic">
                                    Written into the contract before Day 1.
                                </p>
                            </div>
                        </div>
                    </div>
                </section>

                {/* TIMELINE — full 97-day plan (7 onboarding + 90 production) */}
                <section>
                    <div className="text-center mb-10">
                        <SectionTag icon={Calendar} label="The full timeline" color="indigo" />
                        <h2 className="text-3xl sm:text-4xl font-black mb-4">From signature to 10K, in 97 days</h2>
                        <p className="text-slate-400 max-w-2xl mx-auto text-sm sm:text-base">
                            7 days of onboarding, then 90 days of compounding production. The clock on the 10K guarantee starts when your first post goes live, not the day you sign.
                        </p>
                    </div>

                    {/* Phase 1: Onboarding header */}
                    <div className="max-w-5xl mx-auto mb-4 px-1">
                        <div className="flex items-center gap-3">
                            <span className="text-indigo-300 text-[10px] sm:text-xs font-bold uppercase tracking-widest">Phase 1 · Onboarding · Days 1 to 7</span>
                            <div className="flex-1 h-px bg-gradient-to-r from-indigo-500/40 to-transparent"></div>
                        </div>
                    </div>

                    {/* Phase 1: 4 onboarding cards */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 max-w-5xl mx-auto mb-10">
                        <div className="rounded-2xl border border-indigo-500/30 bg-indigo-900/10 p-5 sm:p-6">
                            <div className="text-indigo-300 text-xs font-bold uppercase tracking-widest mb-2">Day 1 to 2</div>
                            <h3 className="text-base sm:text-lg font-bold text-white mb-1.5">Kickoff + research</h3>
                            <p className="text-slate-300 text-sm leading-snug">Call with your team. Lock the ICP definition, positioning, voice, hooks, and reference profiles.</p>
                        </div>
                        <div className="rounded-2xl border border-indigo-500/30 bg-indigo-900/10 p-5 sm:p-6">
                            <div className="text-indigo-300 text-xs font-bold uppercase tracking-widest mb-2">Day 3 to 4</div>
                            <h3 className="text-base sm:text-lg font-bold text-white mb-1.5">AI clone training</h3>
                            <p className="text-slate-300 text-sm leading-snug">25-minute recording session with your designated team member. The only filming you do in 90 days.</p>
                        </div>
                        <div className="rounded-2xl border border-indigo-500/30 bg-indigo-900/10 p-5 sm:p-6">
                            <div className="text-indigo-300 text-xs font-bold uppercase tracking-widest mb-2">Day 5 to 6</div>
                            <h3 className="text-base sm:text-lg font-bold text-white mb-1.5">Notion content system</h3>
                            <p className="text-slate-300 text-sm leading-snug">Your dedicated Notion workspace built: scripts pipeline, posting calendar, ICP tracker, daily DM digest.</p>
                        </div>
                        <div className="rounded-2xl border border-indigo-500/30 bg-indigo-900/10 p-5 sm:p-6">
                            <div className="text-indigo-300 text-xs font-bold uppercase tracking-widest mb-2">Day 7</div>
                            <h3 className="text-base sm:text-lg font-bold text-white mb-1.5">Onboarding complete</h3>
                            <p className="text-slate-300 text-sm leading-snug">Notion live, AI clone trained, ICP locked, first batch of scripts approved. Ready to publish.</p>
                        </div>
                    </div>

                    {/* CLOCK STARTS callout */}
                    <div className="max-w-3xl mx-auto mb-10 rounded-2xl border-2 border-amber-500/50 bg-gradient-to-br from-amber-900/20 to-slate-900/60 p-5 sm:p-6 text-center shadow-[0_0_30px_-10px_rgba(245,158,11,0.35)]">
                        <div className="text-amber-300 text-[10px] sm:text-xs font-bold uppercase tracking-widest mb-2">Day 8</div>
                        <div className="text-xl sm:text-2xl font-black text-white mb-2">First post goes live · 90-day clock starts</div>
                        <p className="text-amber-50/80 text-xs sm:text-sm leading-relaxed max-w-xl mx-auto">
                            The guarantee window opens the day your first video publishes, not the day you sign. You get the full 90 days of compounding distribution.
                        </p>
                    </div>

                    {/* Phase 2: Production header */}
                    <div className="max-w-5xl mx-auto mb-4 px-1">
                        <div className="flex items-center gap-3">
                            <span className="text-emerald-300 text-[10px] sm:text-xs font-bold uppercase tracking-widest">Phase 2 · Production · 90 days of posting</span>
                            <div className="flex-1 h-px bg-gradient-to-r from-emerald-500/40 to-transparent"></div>
                        </div>
                    </div>

                    {/* Phase 2: 4 production cards */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 max-w-5xl mx-auto">
                        <div className="rounded-2xl border border-emerald-500/30 bg-emerald-900/10 p-5 sm:p-6">
                            <div className="text-emerald-300 text-xs font-bold uppercase tracking-widest mb-2">Week 1 of posting</div>
                            <h3 className="text-base sm:text-lg font-bold text-white mb-1.5">Launch + calibrate</h3>
                            <p className="text-slate-300 text-sm leading-snug">First 7 to 8 videos go live across all 5 platforms. Hook variants tested. Baseline engagement data starts populating the Notion dashboard.</p>
                        </div>
                        <div className="rounded-2xl border border-emerald-500/30 bg-emerald-900/10 p-5 sm:p-6">
                            <div className="text-emerald-300 text-xs font-bold uppercase tracking-widest mb-2">Weeks 2 to 4 · Month 1</div>
                            <h3 className="text-base sm:text-lg font-bold text-white mb-1.5">Rhythm + top hooks</h3>
                            <p className="text-slate-300 text-sm leading-snug">~30 videos shipped. Best-performing hooks identified and scripted forward. ICP-tagged views climbing. First DMs from in-ICP accounts surface.</p>
                        </div>
                        <div className="rounded-2xl border border-emerald-500/30 bg-emerald-900/10 p-5 sm:p-6">
                            <div className="text-emerald-300 text-xs font-bold uppercase tracking-widest mb-2">Weeks 5 to 8 · Month 2</div>
                            <h3 className="text-base sm:text-lg font-bold text-white mb-1.5">Funnel goes live</h3>
                            <p className="text-slate-300 text-sm leading-snug">Landing page, DM auto-reply, lead capture, calendar, and nurture installed. ~60 videos cumulative. First qualified leads land.</p>
                        </div>
                        <div className="rounded-2xl border border-emerald-500/30 bg-emerald-900/10 p-5 sm:p-6">
                            <div className="text-emerald-300 text-xs font-bold uppercase tracking-widest mb-2">Weeks 9 to 12 · Month 3</div>
                            <h3 className="text-base sm:text-lg font-bold text-white mb-1.5">Compound + 10K</h3>
                            <p className="text-slate-300 text-sm leading-snug">90 videos shipped total. Funnel optimized. Qualified leads flowing into your calendar. 10K-followers guarantee target reached.</p>
                        </div>
                    </div>
                </section>

                {/* What you don't worry about + what we'd need */}
                <section>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5 sm:gap-6 max-w-5xl mx-auto">
                        <div className="rounded-2xl border border-emerald-500/30 bg-emerald-900/10 p-6 sm:p-8">
                            <div className="flex items-center gap-2 mb-4">
                                <Inbox className="w-5 h-5 text-emerald-300" />
                                <h3 className="text-lg sm:text-xl font-bold text-white">What you don't worry about</h3>
                            </div>
                            <ul className="space-y-2.5">
                                <Check>Recording video every day</Check>
                                <Check>Writing scripts or captions</Check>
                                <Check>Editing or posting</Check>
                                <Check>Funnel build or maintenance</Check>
                                <Check>Dashboard tracking across platforms</Check>
                            </ul>
                        </div>

                        <div className="rounded-2xl border border-indigo-500/30 bg-indigo-900/10 p-6 sm:p-8">
                            <div className="flex items-center gap-2 mb-4">
                                <HeartHandshake className="w-5 h-5 text-indigo-300" />
                                <h3 className="text-lg sm:text-xl font-bold text-white">What we'd need from you</h3>
                            </div>
                            <ol className="space-y-3 text-sm text-slate-200 leading-relaxed list-none">
                                <li className="flex gap-3">
                                    <span className="text-indigo-300 font-bold flex-shrink-0">1.</span>
                                    <span><span className="font-semibold text-white">One 25-minute recording session</span> with your designated team member in Week 3 of Month 1.</span>
                                </li>
                                <li className="flex gap-3">
                                    <span className="text-indigo-300 font-bold flex-shrink-0">2.</span>
                                    <span><span className="font-semibold text-white">Written permission</span> to use the team member's likeness for AI-clone rendering.</span>
                                </li>
                                <li className="flex gap-3">
                                    <span className="text-indigo-300 font-bold flex-shrink-0">3.</span>
                                    <span><span className="font-semibold text-white">Brand assets:</span> logo, colors, existing case studies, automation screenshots.</span>
                                </li>
                                <li className="flex gap-3">
                                    <span className="text-indigo-300 font-bold flex-shrink-0">4.</span>
                                    <span><span className="font-semibold text-white">ICP sign-off</span> in Week 1 so targeting is unambiguous.</span>
                                </li>
                            </ol>
                        </div>
                    </div>
                </section>

                {/* FAQ — 4 only */}
                <section>
                    <div className="max-w-3xl mx-auto">
                        <div className="text-center mb-8">
                            <SectionTag icon={Sparkles} label="FAQ" color="indigo" />
                            <h2 className="text-2xl sm:text-3xl font-black mb-3">Questions from the June 1 call</h2>
                        </div>

                        <div className="space-y-3">
                            {faqs.map((item, i) => (
                                <AccordionItem
                                    key={i}
                                    title={item.title}
                                    body={item.body}
                                    open={openIdx === i}
                                    onToggle={() => toggle(i)}
                                />
                            ))}
                        </div>
                    </div>
                </section>

                {/* FINAL CTA */}
                <section className="text-center pb-10">
                    <div className="max-w-2xl mx-auto bg-gradient-to-br from-indigo-900/50 to-slate-900/80 border border-indigo-500/30 rounded-3xl p-8 sm:p-10 md:p-14 shadow-2xl">
                        <Rocket className="w-9 h-9 sm:w-10 sm:h-10 text-indigo-400 mx-auto mb-4" />
                        <h2 className="text-2xl sm:text-3xl md:text-4xl font-black mb-4 text-white">Ready when you are</h2>
                        <p className="text-slate-300 text-base sm:text-lg mb-8">
                            One offer. ₹3 lakhs for 3 months. Everything done for you. Let's lock the start.
                        </p>
                        <StartCTA />
                    </div>
                </section>

                {/* FOOTER */}
                <footer className="text-center text-slate-500 text-xs sm:text-sm pt-8 border-t border-white/10">
                    <div className="font-bold text-white/70 mb-2">SocialSEO LLP</div>
                    <div className="mb-1">Dubai + Delhi</div>
                    <div className="mb-2 max-w-2xl mx-auto px-4">
                        Distribution engineer for founders and agencies. AI-clone realism case study: Kush Desai, 28 to 24K in 4 months.
                    </div>
                    <div>
                        <a href="mailto:suresh@socialseo.in" className="text-indigo-300 hover:text-indigo-200 transition">suresh@socialseo.in</a>
                    </div>
                </footer>

            </main>
        </div>
    );
}
